import React, { useState, useMemo } from 'react';
import {
  startOfMonth,
  startOfWeek,
  addDays,
  eachDayOfInterval,
  format,
  isSameMonth,
  isSameDay,
  isToday,
  addMonths,
  subMonths
} from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { commitmentDateToCalendarKey } from '@/utils/commitmentCalendarDate';

export interface Commitment {
  id: string;
  title: string;
  date: Date;
  budgetName?: string;
}

export type CommitmentCalendarDensity = 'default' | 'narrow';

interface CommitmentCalendarProps {
  commitments: Commitment[];
  selectedDate?: Date | null;
  onDateSelect?: (date: Date) => void;
  /** Single-week strip (sticky mobile header). */
  compact?: boolean;
  /**
   * `narrow`: use mobile calendar styling at every breakpoint (borders, cell size, pills hidden).
   * Use inside a narrow container (e.g. max-w-[410px]) on desktop.
   */
  density?: CommitmentCalendarDensity;
  currentMonth?: Date;
  onMonthChange?: (month: Date) => void;
  className?: string;
}

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MAX_VISIBLE_ITEMS = 2;

function CommitmentCalendar({
  commitments,
  selectedDate,
  onDateSelect,
  compact = false,
  density = 'default',
  currentMonth: controlledMonth,
  onMonthChange,
  className = ''
}: CommitmentCalendarProps) {
  const isNarrow = density === 'narrow';
  const [uncontrolledMonth, setUncontrolledMonth] = useState(new Date());
  const currentMonth = controlledMonth ?? uncontrolledMonth;

  console.log('commitments', commitments);

  const updateMonth = (month: Date) => {
    onMonthChange ? onMonthChange(month) : setUncontrolledMonth(month);
  };

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calEnd = addDays(calStart, 41);
    return eachDayOfInterval({ start: calStart, end: calEnd });
  }, [currentMonth]);

  const commitmentsByDate = useMemo(() => {
    const map = new Map<string, Commitment[]>();
    commitments.forEach((c) => {
      const key = commitmentDateToCalendarKey(c.date);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(c);
    });
    return map;
  }, [commitments]);

  const weeks = useMemo(() => {
    const result: Date[][] = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      result.push(calendarDays.slice(i, i + 7));
    }
    return result;
  }, [calendarDays]);

  const displayWeeks = useMemo(() => {
    if (!compact) return weeks;
    const target = selectedDate || new Date();
    const matched = weeks.find((week) => week.some((day) => isSameDay(day, target)));
    return matched ? [matched] : weeks.slice(0, 1);
  }, [compact, selectedDate, weeks]);

  const goToPrevMonth = () => updateMonth(subMonths(currentMonth, 1));
  const goToNextMonth = () => updateMonth(addMonths(currentMonth, 1));

  const prevMonth = subMonths(currentMonth, 1);
  const nextMonth = addMonths(currentMonth, 1);

  const getDateCommitments = (day: Date) => {
    return commitmentsByDate.get(format(day, 'yyyy-MM-dd')) || [];
  };

  const getDayState = (day: Date) => {
    const inMonth = isSameMonth(day, currentMonth);
    const today = isToday(day);
    const selected = selectedDate ? isSameDay(day, selectedDate) : false;
    const dayCommitments = getDateCommitments(day);
    const hasEvents = dayCommitments.length > 0;

    return { inMonth, today, selected, hasEvents, dayCommitments };
  };

  return (
    <div className={cn('flex flex-col flex-1', isNarrow ? 'gap-0' : 'gap-2', className)}>
      {/* Navigation */}
      <div className={cn('flex items-center justify-between py-2', isNarrow ? 'px-2' : 'px-6 md:px-1')}>
        <button className="flex items-center text-base font-semibold gap-0.5" onClick={goToPrevMonth}>
          <ChevronLeft className="text-primary h-5 w-5" />
          <span>{format(prevMonth, 'MMM')}</span>
        </button>
        <span className="text-base font-bold">{format(currentMonth, 'MMM yyyy')}</span>
        <button className="flex items-center text-base font-semibold gap-0.5" onClick={goToNextMonth}>
          <span>{format(nextMonth, 'MMM')}</span>
          <ChevronRight className="text-primary h-5 w-5" />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7">
        {WEEKDAYS.map((day) => (
          <div key={day} className="text-center text-xs font-medium text-lightGray-100 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="flex flex-col">
        {displayWeeks.map((week, weekIdx) => (
          <div
            key={weekIdx}
            className={cn(
              'grid grid-cols-7',
              compact ? 'min-h-0' : isNarrow ? 'min-h-[40px]' : 'min-h-[48px] md:min-h-[100px]'
            )}
          >
            {week.map((day) => {
              const { inMonth, today, selected, hasEvents, dayCommitments } = getDayState(day);
              const totalCount = dayCommitments.length;
              const overflow = totalCount > MAX_VISIBLE_ITEMS;
              const key = format(day, 'yyyy-MM-dd');

              return (
                <div
                  key={key}
                  className={cn(
                    'flex flex-col items-center cursor-pointer transition-colors hover:bg-muted/30',
                    isNarrow ? 'p-1 border-0' : 'p-1 md:p-2 border-0 md:border',
                    !isNarrow && selected && 'md:border md:border-primary',
                    !inMonth && 'bg-muted/10'
                  )}
                  onClick={() => onDateSelect?.(day)}
                >
                  {/* Date number */}
                  <div
                    className={cn(
                      'flex items-center justify-center rounded-full shrink-0 transition-colors',
                      isNarrow
                        ? 'w-8 h-9 text-sm'
                        : 'w-9 h-10 md:h-9 md:w-11 text-base md:text-xl md:font-semibold md:mb-1',
                      !inMonth && 'text-secondary font-medium',
                      inMonth && !today && !selected && !hasEvents && 'text-foreground',
                      today && !selected && !hasEvents && 'border border-primary text-primary',
                      hasEvents &&
                        !selected &&
                        inMonth &&
                        !today &&
                        (isNarrow
                          ? 'bg-lightPurple-200 text-[#333]'
                          : 'bg-lightPurple-200 text-[#333] md:bg-lightPurple-200 md:text-primary'),
                      hasEvents &&
                        !selected &&
                        inMonth &&
                        today &&
                        (isNarrow
                          ? 'bg-primary text-white'
                          : 'bg-primary text-white md:bg-lightPurple-200 md:text-primary'),
                      selected && inMonth && 'bg-primary text-white'
                    )}
                  >
                    {format(day, 'd')}
                  </div>

                  {/* Commitment items — default density: desktop only; narrow: hidden (mobile parity) */}
                  {inMonth && totalCount > 0 && !isNarrow && (
                    <div className="hidden md:flex flex-col gap-0.5 w-full">
                      {!overflow ? (
                        dayCommitments.map((c) => (
                          <div
                            key={c.id}
                            className="text-xs leading-tight text-primary bg-primary text-white font-semibold border border-primary rounded-full px-2 py-0.5 truncate text-center"
                          >
                            {c.title}
                          </div>
                        ))
                      ) : (
                        <>
                          <div className="text-xs leading-tight text-primary bg-primary text-white font-semibold border border-primary rounded-full px-2 py-0.5 truncate text-center">
                            {dayCommitments[0].title}
                          </div>
                          <div className="text-xs leading-tight text-primary font-semibold border border-primary rounded-full px-2 py-0.5 truncate text-center">
                            +{totalCount - 1}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export default CommitmentCalendar;
